import type { TextInputProps } from '@mantine/core'
import {
  Button,
  Grid,
  Group,
  List,
  Loader,
  Paper,
  Radio,
  Stack,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
} from '@mantine/core'
import type { UseFormReturnType } from '@mantine/form'
import { useHotkeys } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import type { AssetTrackerSchema } from '@opsopti/shared'
import {
  assetTrackerSchema,
  castAssetTrackerToForm,
  formatDate,
  isFieldsListValid,
  omit,
  useAssetTrackerCreateMutation,
  useAssetTrackerForm,
  useAssetTrackerQuery,
  useAssetTrackerUpdateMutation,
  useGlobalState,
} from '@opsopti/shared'
import type { CreateAssetTrackerResponse, UpdateAssetTrackerResponse } from '@opsopti/shared/src/services/api/api.types'
import { useAreFormQueriesFetched } from '@opsopti/shared/src/utils/tanstackHelpers'
import {
  IconDownload,
  IconNfc,
  IconNumber1,
  IconNumber2,
  IconNumber3,
  IconNumber4,
  IconPrinter,
  IconRefresh,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { observer } from 'mobx-react-lite'
import { QRCodeSVG } from 'qrcode.react'
import qs from 'qs'
import { createContext, Fragment, useContext, useEffect, useMemo, useRef } from 'react'
import { useReactToPrint } from 'react-to-print'
import { v4 as uuidv4 } from 'uuid'
import type { z } from 'zod'

import { RadioCard } from '@/components/RadioCard'
import { Screen, type ScreenProps } from '@/components/Screen'
import { SectionedForm, type SectionedFormProps } from '@/components/SectionedForm'
import { colors } from '@/configs/colors'
import type { RouteParams } from '@/configs/routes'
import { theme } from '@/configs/theme'
import {
  getFormScreenTitle,
  getFormSubmitLabel,
  showErrorNotification,
  showSuccessNotification,
} from '@/utils/componentCommons'
import { goBack } from '@/utils/navigation'

import { TableScreenDescription, tableScreenIcon } from './TableScreen'

type AssetSentryTrackerFormScreenProps = {
  type?: 'screen' | 'drawer'
  params?: RouteParams['assetSentryTrackerEdit'] | RouteParams['assetSentryTrackerNew']
  goBackFn?: () => void
  onAfterSubmitFn?: (tracker: CreateAssetTrackerResponse | UpdateAssetTrackerResponse) => void
}

type FormSection = {
  id: string | null
  type: 'screen' | 'drawer'
  isNew: boolean
  form: UseFormReturnType<z.infer<AssetTrackerSchema>>
  isMutating: boolean
  submitFn: () => Promise<void>
  goBackFn: () => void
}

const AssetTrackerFormDataContext = createContext<FormSection>({} as FormSection)
const useAssetTrackerFormData = () => useContext(AssetTrackerFormDataContext)

export const AssetSentryTrackerFormScreen = observer(function AssetSentryTrackerFormScreen(
  props: AssetSentryTrackerFormScreenProps,
) {
  const { uiStore } = useGlobalState()

  const id = props.params?.id ?? null
  const isNew = useMemo(() => !id, [id])
  const type = props.type ?? 'screen'

  const { form } = useAssetTrackerForm()

  const trackerQuery = useAssetTrackerQuery({ params: { id } })
  const trackerCreateMutation = useAssetTrackerCreateMutation({
    onErrorNotification: showErrorNotification,
    onSuccessNotification: showSuccessNotification,
  })
  const trackerUpdateMutation = useAssetTrackerUpdateMutation({
    onErrorNotification: showErrorNotification,
    onSuccessNotification: showSuccessNotification,
  })

  const areQueriesLoaded = useAreFormQueriesFetched([!isNew ? trackerQuery.isFetched : null], 200)

  useEffect(() => {
    if (areQueriesLoaded) {
      form.initialize(castAssetTrackerToForm(trackerQuery.data))
      uiStore.hideLoadingOverlay()
    } else {
      uiStore.showLoadingOverlay()
    }
  }, [areQueriesLoaded])

  useEffect(() => {
    return () => {
      modals.close('nfc-scan-modal')
      uiStore.hideLoadingOverlay()
    }
  }, [])

  useHotkeys([['mod+S', submitFn]], [])

  const goBackFn = props.goBackFn ?? goBack

  async function submitFn() {
    const validations = form.validate()

    if (validations.hasErrors) {
      showErrorNotification({ title: 'Form Errors', message: 'Please fill out all required fields before submitting.' })
      return
    }

    const values = form.getValues()
    const trackerValues = omit(values, 'asset')

    let response: CreateAssetTrackerResponse | UpdateAssetTrackerResponse | undefined

    if (isNew) {
      response = await trackerCreateMutation.mutateAsync(trackerValues)
    } else {
      response = await trackerUpdateMutation.mutateAsync({ ...trackerValues, id: id! })
    }

    props.onAfterSubmitFn?.(response)

    goBackFn()
  }

  const sections = useMemo(
    () =>
      [
        {
          id: 'general',
          title: 'General Details',
          subtitle: 'Basic details about the tracker.',
          collapsible: false,
          valid: isFieldsListValid(form, ['identifier', 'type']),
          ContentComponent: GeneralFields,
        },
        {
          id: 'additional',
          title: 'Additional Information',
          subtitle: 'Visibility settings, notes, and other information.',
          collapsible: false,
          valid: isFieldsListValid(form, ['visibilityStatus']),
          ContentComponent: AdditionalFields,
        },
        // {
        //   id: 'asset',
        //   title: 'Asset',
        //   subtitle: 'The asset that this tracker is associated with.',
        //   collapsible: false,
        //   ContentComponent: AssetFields,
        // },
        {
          id: 'actions',
          collapsible: false,
          ContentComponent: FormActions,
        },
      ] satisfies SectionedFormProps['sections'],
    [form],
  )

  const isMutating = trackerCreateMutation.isPending || trackerUpdateMutation.isPending
  const FormWrapper = type === 'screen' ? Screen : Fragment
  const FormWrapperProps: ScreenProps =
    type === 'screen'
      ? {
          maw: 1440,
          title: getFormScreenTitle(id, 'Tracker', trackerQuery.data?.typeWithIdentifier ?? ''),
          icon: tableScreenIcon['groups'],
          description: <TableScreenDescription entity="groups" />,
        }
      : ({} as ScreenProps)

  return (
    <FormWrapper {...FormWrapperProps}>
      <AssetTrackerFormDataContext.Provider value={{ id, isNew, form, submitFn, goBackFn, isMutating, type }}>
        <SectionedForm sections={sections} sectionsWithBorder={type === 'screen'} />
      </AssetTrackerFormDataContext.Provider>
    </FormWrapper>
  )
})

function GeneralFields() {
  const props = useAssetTrackerFormData()
  const { api } = useGlobalState()

  const qrCode = useRef<SVGSVGElement>(null)

  const qrCodeTitle = useMemo(
    () =>
      ['qr-code', formatDate(Date.now(), { format: 'MM-DD-YYYY-HH-mm' }), props.form.getValues().identifier].join('-'),
    [props.form.getValues().identifier],
  )

  const printQrCode = useReactToPrint({ documentTitle: qrCodeTitle })

  async function downloadQrCode(svg: SVGSVGElement | null, width = 1000, height = 1000) {
    if (!svg) return

    const xml = new XMLSerializer().serializeToString(svg)

    const img = new Image()
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`

    await new Promise((resolve, reject) => {
      img.onload = resolve
      img.onerror = reject
    })

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const ctx = canvas.getContext('2d')

    if (!ctx) return

    ctx.drawImage(img, 0, 0, width, height)

    const link = document.createElement('a')
    link.download = qrCodeTitle
    link.href = canvas.toDataURL('image/png')
    link.click()
  }

  async function openNfcScanModal() {
    const response = await api.safeRequest(api.createAssetTrackerTempRegistration)

    if (response.ok) {
      modals.open({
        modalId: 'nfc-scan-modal',
        centered: true,
        zIndex: 239,
        title: (
          <Group align="center">
            <Loader type="bars" size="sm" />
            <Text fw={700} fs={theme.rem(20)}>
              Waiting for NFC Scan...
            </Text>
          </Group>
        ),
        size: 'xl',
        closeOnEscape: false,
        closeOnClickOutside: false,
        children: (
          <NFCScanModal
            id={response.data.doc.id}
            onSuccessfulScan={identifier => {
              props.form.setFieldValue('identifier', identifier)
              modals.close('nfc-scan-modal')
            }}
          />
        ),
      })
    }
  }

  const IdentifierTextInputProps: TextInputProps =
    props.form.getValues().type === 'qr'
      ? {
          rightSectionWidth: props.isNew ? 196 : 220,
          rightSection: (
            <Button
              size="sm"
              leftSection={<IconRefresh size={22} />}
              onClick={() => props.form.setFieldValue('identifier', uuidv4())}>
              {props.isNew ? 'Generate' : 'Re-Generate'} QR Code
            </Button>
          ),
        }
      : props.form.getValues().type === 'nfc'
        ? {
            rightSectionWidth: 166,
            rightSection: (
              <Button size="sm" leftSection={<IconNfc size={22} />} onClick={openNfcScanModal}>
                Scan NFC Tag
              </Button>
            ),
          }
        : {}

  const RadioWrapper = props.type === 'screen' ? Group : Stack
  const RadioComponent = props.type === 'screen' ? RadioCard : Radio

  return (
    <Grid gutter={props.type === 'screen' ? 'xxl' : 'xl'}>
      <Grid.Col span={12}>
        <Radio.Group
          label="Tracker Type"
          withAsterisk
          {...props.form.getInputProps('type')}
          onChange={t => {
            props.form.setFieldValue('identifier', '')
            props.form.setFieldValue('type', t as any)
          }}>
          <RadioWrapper align="stretch">
            <RadioComponent
              value={assetTrackerSchema.shape.type.enum.nfc}
              label="NFC Tag"
              description="A physical NFC chip that can be scanned by a phone."
            />

            <RadioComponent
              value={assetTrackerSchema.shape.type.enum.qr}
              label="QR Code"
              description="A printed sticker that can be scanned by a phone."
            />

            <RadioComponent
              value={assetTrackerSchema.shape.type.enum.other}
              label="Other"
              description="Miscellaneous method for tracking an asset (e.g. phone number, MAC address, etc)."
            />
          </RadioWrapper>
        </Radio.Group>
      </Grid.Col>

      <Grid.Col span={12}>
        <Stack>
          <TextInput
            withAsterisk
            label="Identifier"
            description="A unique identifier that is associated with the tracker. For NFC, this will be the tag's ID. For QR code, this will be any unique string. For other trackers (e.g. phone), this can be a phone number."
            {...props.form.getInputProps('identifier')}
            {...(props.type === 'screen' ? IdentifierTextInputProps : {})}
          />

          {props.type === 'drawer' && IdentifierTextInputProps.rightSection}
        </Stack>
      </Grid.Col>

      {props.form.getValues().type === 'qr' && !!props.form.getValues().identifier && (
        <Grid.Col span={12}>
          <Paper p="lg" radius="sm" shadow="none" bd="1px solid gray.4">
            <Stack justify="space-evenly" align="center" gap="xl">
              <QRCodeSVG
                ref={qrCode}
                value={props.form.getValues().identifier}
                level="H"
                size={props.type === 'screen' ? 380 : 320}
                marginSize={1}
                fgColor={colors.port[7]}
              />

              <Group justify="center">
                <Button
                  size="sm"
                  variant="light"
                  leftSection={<IconPrinter />}
                  onClick={() => printQrCode(() => qrCode.current)}>
                  Print QR Code
                </Button>

                <Button
                  size="sm"
                  variant="light"
                  leftSection={<IconDownload />}
                  onClick={() => downloadQrCode(qrCode.current)}>
                  Download QR Code
                </Button>
              </Group>
            </Stack>
          </Paper>
        </Grid.Col>
      )}
    </Grid>
  )
}

function AdditionalFields() {
  const props = useAssetTrackerFormData()

  const RadioWrapper = props.type === 'screen' ? Group : Stack
  const RadioComponent = props.type === 'screen' ? RadioCard : Radio

  return (
    <Grid gutter={props.type === 'screen' ? 'xxl' : 'xl'}>
      <Grid.Col span={12}>
        <Radio.Group label="Visibility" withAsterisk {...props.form.getInputProps('visibilityStatus')}>
          <RadioWrapper align="stretch">
            <RadioComponent
              value={assetTrackerSchema.shape.visibilityStatus.enum.active}
              label="Active"
              description="Tracker is active and ready to be used."
            />

            <RadioComponent
              value={assetTrackerSchema.shape.visibilityStatus.enum.archived}
              label="Archived"
              description="Tracker is archived and no longer in use."
            />
          </RadioWrapper>
        </Radio.Group>
      </Grid.Col>

      <Grid.Col span={12}>
        <Textarea
          label="Notes For Supervisors"
          autosize
          minRows={4}
          description="Notes that can be used for internal purposes. Only visible to Supervisors."
          {...props.form.getInputProps('internalNotes')}
        />
      </Grid.Col>
    </Grid>
  )
}

function FormActions() {
  const props = useAssetTrackerFormData()

  return (
    <Stack gap="xxl">
      <Group gap="xl">
        <Button size="lg" radius="sm" onClick={props.submitFn} loading={props.isMutating}>
          {getFormSubmitLabel(props.id, 'Tracker')}
        </Button>

        {!props.isMutating && (
          <Button size="sm" radius="sm" variant="light" color="gray" onClick={props.goBackFn}>
            Cancel
          </Button>
        )}
      </Group>
    </Stack>
  )
}

function NFCScanModal(props: { id: string; onSuccessfulScan: (identifier: string) => void }) {
  const { api } = useGlobalState()

  const tempRegistryQuery = useQuery({
    queryFn: () => api.getAssetTrackerTempRegistration({ id: props.id }),
    queryKey: ['asset-sentry', 'asset-tracker-temp-registry', props.id],
    refetchInterval: 2 * 1000,
    refetchIntervalInBackground: true,
  })

  useEffect(() => {
    if (!!tempRegistryQuery.data?.nfcIdentifier) {
      props.onSuccessfulScan(tempRegistryQuery.data.nfcIdentifier)
    }
  }, [tempRegistryQuery.data?.nfcIdentifier])

  const qrValue = `opsopti://dashboard/asset-scanner?${qs.stringify({ mode: 'single', purpose: 'link-tracker-via-web', tmpTrackerId: props.id })}`

  console.info(qrValue)

  return (
    <Stack gap="xl">
      <List spacing="md" center>
        <List.Item
          icon={
            <ThemeIcon size={24} radius="xl">
              <IconNumber1 style={{ width: theme.rem(16), height: theme.rem(16) }} />
            </ThemeIcon>
          }>
          {`Use your phone's camera to scan the QR code below.`}
        </List.Item>

        <List.Item
          icon={
            <ThemeIcon size={24} radius="xl">
              <IconNumber2 style={{ width: theme.rem(16), height: theme.rem(16) }} />
            </ThemeIcon>
          }>
          {`Wait for the app to open.`}
        </List.Item>

        <List.Item
          icon={
            <ThemeIcon size={24} radius="xl">
              <IconNumber3 style={{ width: theme.rem(16), height: theme.rem(16) }} />
            </ThemeIcon>
          }>
          {`Scan the NFC tag with your phone.`}
        </List.Item>

        <List.Item
          icon={
            <ThemeIcon size={24} radius="xl">
              <IconNumber4 style={{ width: theme.rem(16), height: theme.rem(16) }} />
            </ThemeIcon>
          }>
          {`After a successful scan, this panel will close and the identifier field will be updated.`}
        </List.Item>
      </List>

      <Paper p="lg" radius="sm" shadow="none" bd="1px solid gray.4">
        <Stack align="center">
          <QRCodeSVG value={qrValue} level="H" size={380} marginSize={1} fgColor={colors.port[7]} />
        </Stack>
      </Paper>

      <Text size="sm" color="dimmed">{`Note: If this panel does not close, exit out and retry the process.`}</Text>
    </Stack>
  )
}
